/**
 * Voucher entry -- one screen, six voucher types.
 *
 * Three things make it feel like Tally rather than a web form:
 *
 *   1. The account code and bill-wise flag appear inline the moment an
 *      account is picked, so the user knows what they are posting against.
 *   2. Enter always means "commit this field and move to the next one",
 *      and nothing blocks on a network call mid-entry.
 *   3. Totals update on every keystroke, so the moment the voucher
 *      balances is visible, not discovered on submit.
 *
 * Permissions shape the screen: an unavailable voucher type is never
 * offered, and a cashier's picker lists only their own till.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useQueryClient } from '@tanstack/react-query'
import {
  Alert, Box, Button, IconButton, Paper, Snackbar, Stack, TextField, Tooltip,
  Typography, useMediaQuery, useTheme,
} from '@mui/material'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'
import AddIcon from '@mui/icons-material/Add'
import CheckIcon from '@mui/icons-material/Check'
import CloseIcon from '@mui/icons-material/Close'
import { useTranslation } from 'react-i18next'

import { postVoucher } from '../api/endpoints'
import { errorMessage } from '../api/client'
import { P, type BillAllocationIn, type DrCr, type Ledger,
         type VoucherLineIn } from '../api/types'
import { useAuth } from '../store/AuthContext'
import { useLedgers } from '../store/LedgerCache'
import { useHotkeys } from '../hotkeys/HotkeyProvider'
import { useSmartBack } from '../hooks/useSmartBack'
import { BALANCES_KEY, useLedgerBalances } from '../store/useLedgerBalances'
import { useGuard, useUnsavedChanges } from '../store/UnsavedChanges'
import { toHijri } from '../reports/hijri'
import { c, tokens } from '../theme'
import Keycap from '../components/Keycap'
import SectionHead from '../components/SectionHead'
import LedgerPicker, { type LedgerPickerHandle } from './LedgerPicker'
import AmountInput, { type AmountHandle } from './AmountInput'
import CreatePartyDialog from './CreatePartyDialog'
import BillAllocationDialog from './BillAllocation'
import { configForSlug, VAT_RATE, VOUCHER_TYPES } from './voucherTypes'
import {
  addMoney, display, eqMoney, isPositive, isZero, mulMoney, subMoney, withSide,
} from './money'
import { today } from '../dates'

interface Row {
  id: number
  dr_cr: DrCr
  code: string
  amount: string
  groups?: string[]
  role?: string
  bills?: BillAllocationIn[]
  /** The code the voucher type prefilled, so it does not count as an edit. */
  seedCode?: string
}

let nextId = 1
const newRow = (dr_cr: DrCr, extra: Partial<Row> = {}): Row =>
  ({ id: nextId++, dr_cr, code: '', amount: '', ...extra })

// See ../dates: toISOString() is UTC, so this used to default a
// voucher to YESTERDAY for anyone keying after midnight in Riyadh.
const todayStr = () => today()

/** Map a semantic role to the seeded chart's code. */
const ROLE_CODE: Record<string, string> = {
  SALES: '4100', VAT_OUTPUT: '2140', PURCHASES: '5100', VAT_INPUT: '1160',
}

export default function VoucherEntry() {
  const { kind: slug = '' } = useParams()
  const nav = useNavigate()
  const { back } = useSmartBack()
  const { t, i18n } = useTranslation()
  const ar = i18n.language === 'ar'
  /** "Debit · مدين": English leads, Arabic follows as a quieter sub-label. */
  const bi = (key: string) => {
    if (ar) return t(key)
    const arabic = i18n.getFixedT('ar')(key)
    return (
      <>{t(key)}{arabic && arabic !== key && (
        <span lang="ar" style={{ fontFamily: 'var(--font-arabic)', fontWeight: 400,
                                 color: c.inkMuted }}> · {arabic}</span>
      )}</>
    )
  }
  const { can, me } = useAuth()
  const { byCode } = useLedgers()
  const balances = useLedgerBalances()
  const guard = useGuard()
  const qc = useQueryClient()
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))

  const config = configForSlug(slug)

  const [date, setDate] = useState(todayStr())
  const [narration, setNarration] = useState('')
  const [rows, setRows] = useState<Row[]>([])
  const [error, setError] = useState<string | null>(null)
  const [saved, setSaved] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const [createOpen, setCreateOpen] = useState(false)
  const [createSeed, setCreateSeed] = useState('')
  const [createForRow, setCreateForRow] = useState<number | null>(null)
  const [billRow, setBillRow] = useState<number | null>(null)
  /** The line the cursor is on, for the "selected account" panel. */
  const [activeRow, setActiveRow] = useState<number | null>(null)

  const pickerRefs = useRef<Record<number, LedgerPickerHandle | null>>({})
  const amountRefs = useRef<Record<number, AmountHandle | null>>({})
  const narrationRef = useRef<HTMLInputElement>(null)

  const resetRows = useCallback(() => {
    if (!config) return
    setRows(config.seed.map((s) => newRow(s.dr_cr, {
      groups: s.groups, role: s.role,
      code: s.role ? (ROLE_CODE[s.role] ?? '') : '',
      seedCode: s.role ? (ROLE_CODE[s.role] ?? '') : '',
    })))
  }, [config])

  // Reset whenever the voucher type changes.
  useEffect(() => {
    if (!config) return
    resetRows()
    setNarration('')
    setError(null)
    setDate(todayStr())
  }, [config, resetRows])

  const totals = useMemo(() => {
    const dr = addMoney(...rows.filter((r) => r.dr_cr === 'D').map((r) => r.amount || '0'))
    const cr = addMoney(...rows.filter((r) => r.dr_cr === 'C').map((r) => r.amount || '0'))
    return { dr, cr, diff: subMoney(dr, cr), balanced: eqMoney(dr, cr) && isPositive(dr) }
  }, [rows])

  const canPost = config ? can(config.permission) : false

  // Anything typed beyond what the voucher type prefilled is work the
  // user would lose by walking away.
  const dirty = useMemo(() =>
    !!narration.trim() || rows.some((r) =>
      (!!r.amount && !isZero(r.amount)) || (!!r.code && r.code !== (r.seedCode ?? ''))),
  [rows, narration])
  useUnsavedChanges(dirty)

  /**
   * How each account moves when this voucher is saved: today's balance,
   * plus this voucher's debits, minus its credits. Only for accounts whose
   * balance this user may see.
   */
  const preview = useMemo(() => {
    const byAcct = new Map<string, string>()
    for (const r of rows) {
      if (!r.code || !r.amount || isZero(r.amount)) continue
      const delta = r.dr_cr === 'D' ? r.amount : subMoney('0', r.amount)
      byAcct.set(r.code, addMoney(byAcct.get(r.code) ?? '0', delta))
    }
    return [...byAcct.entries()].flatMap(([code, delta]) => {
      const before = balances.of(code)
      if (before === undefined) return []
      return [{ code, before, after: addMoney(before, delta) }]
    })
  }, [rows, balances])

  // A cashier may only touch their own till; the picker shows nothing else.
  const cashBankAllow = me?.allowed_cash_bank ?? []
  const partyAllow = me?.allowed_parties ?? []

  const allowFor = useCallback((groups?: string[]) => {
    if (!groups?.length) return undefined
    if (groups.some((g) => g === 'CASH' || g === 'BANK')) {
      return cashBankAllow.length ? cashBankAllow : undefined
    }
    if (groups.some((g) => g === 'DEBTORS' || g === 'CREDIT')) {
      return partyAllow.length ? partyAllow : undefined
    }
    return undefined
  }, [cashBankAllow, partyAllow])

  const setRow = useCallback((id: number, patch: Partial<Row>) => {
    setRows((prev) => prev.map((r) => (r.id === id ? { ...r, ...patch } : r)))
  }, [])

  const addRow = useCallback((dr_cr: DrCr = 'D') => {
    const r = newRow(dr_cr)
    setRows((prev) => [...prev, r])
    setTimeout(() => pickerRefs.current[r.id]?.focus(), 30)
  }, [])

  const removeRow = useCallback((id: number) => {
    setRows((prev) => (prev.length <= 2 ? prev : prev.filter((r) => r.id !== id)))
  }, [])

  /**
   * Sales and purchase: typing the taxable amount fills VAT and the party
   * line automatically. 100 becomes 15.00 VAT and 115.00 receivable.
   */
  const applyAmount = useCallback((id: number, value: string) => {
    setRows((prev) => {
      const next = prev.map((r) => (r.id === id ? { ...r, amount: value } : r))
      if (!config || (config.kind !== 'SALES' && config.kind !== 'PURCHASE')) return next
      const base = next.find((r) => r.role === 'SALES' || r.role === 'PURCHASES')
      if (!base || base.id !== id) return next
      const vatRow = next.find((r) => r.role === 'VAT_OUTPUT' || r.role === 'VAT_INPUT')
      const partyRow = next.find((r) =>
        r.groups?.some((g) => g === 'DEBTORS' || g === 'CREDIT'))
      const vat = mulMoney(value || '0', VAT_RATE)
      const gross = addMoney(value || '0', vat)
      return next.map((r) => {
        if (vatRow && r.id === vatRow.id) return { ...r, amount: vat }
        if (partyRow && r.id === partyRow.id) return { ...r, amount: gross }
        return r
      })
    })
  }, [config])

  const focusAmount = (id: number) =>
    setTimeout(() => amountRefs.current[id]?.focus(), 20)

  const advanceFrom = (index: number) => {
    const next = rows[index + 1]
    if (next) setTimeout(() => pickerRefs.current[next.id]?.focus(), 20)
    else narrationRef.current?.focus()
  }

  const save = useCallback(async () => {
    if (!config || busy) return
    setError(null)

    const lines: VoucherLineIn[] = rows
      .filter((r) => r.code && !isZero(r.amount || '0'))
      .map((r) => ({
        dr_cr: r.dr_cr, amount: r.amount, ledger_code: r.code,
        bill_allocations: r.bills?.length ? r.bills : undefined,
      }))

    if (lines.length < 2) { setError(t('entry.needTwoLines')); return }
    if (!eqMoney(
      addMoney(...lines.filter((l) => l.dr_cr === 'D').map((l) => l.amount)),
      addMoney(...lines.filter((l) => l.dr_cr === 'C').map((l) => l.amount)),
    )) { setError(t('entry.notBalanced')); return }

    setBusy(true)
    try {
      const v = await postVoucher({
        voucher_kind: config.kind, voucher_date: date,
        narration: narration || undefined, lines,
      })
      setSaved(v.voucher_no)
      void qc.invalidateQueries({ queryKey: BALANCES_KEY })
      void qc.invalidateQueries({ queryKey: ['outstanding'] })
      void qc.invalidateQueries({ queryKey: ['daybook'] })
      void qc.invalidateQueries({ queryKey: ['trial-balance'] })
      void qc.invalidateQueries({ queryKey: ['ledger'] })
      void qc.invalidateQueries({ queryKey: ['balance-sheet'] })
      // Straight into the next one -- data entry is repetitive by nature.
      resetRows()
      setNarration('')
      setTimeout(() => {
        const first = Object.values(pickerRefs.current)[config.focusRow]
        first?.focus()
      }, 60)
    } catch (e) {
      setError(errorMessage(e))
    } finally {
      setBusy(false)
    }
  }, [config, busy, rows, date, narration, qc, resetRows, t])

  useHotkeys('voucher-entry', [
    { combo: 'ctrl+enter', label: t('entry.save'), allowInInput: true,
      handler: () => void save() },
    // Was nav('/'), labelled "back". From Outstanding -> ledger ->
    // voucher that returned the user to the home screen and lost the
    // report they were reading.
    { combo: 'escape', label: t('common.back'), allowInInput: true,
      handler: () => guard(back) },
    { combo: 'alt+a', label: t('entry.addLine'), allowInInput: true,
      handler: () => addRow() },
    { combo: 'alt+b', label: t('bills.title'), allowInInput: true,
      handler: () => {
        // Allocate against whichever bill-wise line is focused, else the
        // first one on the voucher.
        const target = rows.find((r) => {
          const l = r.code ? byCode(r.code) : undefined
          return l?.maintain_bill_wise
        })
        if (target) setBillRow(target.id)
      } },
    ...VOUCHER_TYPES.filter((v) => can(v.permission)).map((v) => ({
      combo: v.combo, label: t(v.labelKey), allowInInput: true,
      handler: () => guard(() => nav(`/vouchers/${v.slug}`)),
    })),
  ])

  if (!config) return <Alert severity="error">{t('entry.unknownType')}</Alert>
  if (!canPost) {
    return (
      <Alert severity="warning">
        {t('entry.noPermission', { type: t(config.labelKey) })}
      </Alert>
    )
  }

  const GRID = isMobile ? '54px 1fr 108px 36px' : '64px 1fr 136px 136px 44px'
  const empty = isZero(totals.dr) && isZero(totals.cr)
  const activeCode = rows.find((r) => r.id === activeRow)?.code
    || rows.find((r) => r.code && r.code !== (r.seedCode ?? ''))?.code || ''
  const activeLed = activeCode ? byCode(activeCode) : undefined
  const activeNow = activeCode ? balances.of(activeCode) : undefined
  const activeAfter = preview.find((p) => p.code === activeCode)?.after ?? activeNow
  const fmt = (v?: string) => {
    if (v === undefined) return '—'
    const w = withSide(v)
    return w.side ? `${w.amount} ${w.side}` : w.amount
  }
  const showPanel = balances.ready

  const balanceChip = empty ? (
    <Typography variant="caption" sx={{ color: c.inkMuted }}>{t('entry.enterAmounts')}</Typography>
  ) : totals.balanced ? (
    <Box component="span" role="status" sx={{
      display: 'inline-flex', alignItems: 'center', gap: 0.75, px: 1.25, py: 0.5,
      borderRadius: 999, fontSize: 12, fontWeight: 600,
      bgcolor: c.successSoft, color: c.successInk }}>
      <CheckIcon sx={{ fontSize: 14 }} aria-hidden />
      {bi('entry.balanced')} · {t('entry.differenceShort', { amount: '0.00' })}
    </Box>
  ) : (
    <Box component="span" role="status" sx={{
      display: 'inline-flex', alignItems: 'center', gap: 0.75, px: 1.25, py: 0.5,
      borderRadius: 999, fontSize: 12, fontWeight: 600,
      bgcolor: c.dangerSoft, color: c.dangerInk }}>
      <CloseIcon sx={{ fontSize: 14 }} aria-hidden />
      {t('entry.notBalancedShort')} · {t('entry.differenceShort', {
        amount: withSide(totals.diff).amount })}
    </Box>
  )

  return (
    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'flex-start' }}>
      <Paper variant="outlined" sx={{ flex: '3 1 620px', minWidth: 0, overflow: 'hidden' }}>
        <SectionHead label={t(config.labelKey)}
          right={<Keycap combo={config.combo} />} />

        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}
          sx={{ px: 2, pt: 1, pb: 1.5 }}>
          <Box>
            <TextField type="date" label={bi('entry.date')} value={date}
              onChange={(e) => setDate(e.target.value)}
              InputLabelProps={{ shrink: true }} sx={{ width: 190 }} />
            {date && (
              <Typography sx={{ fontSize: 12, color: c.inkMuted, mt: 0.5, px: 0.25 }}>
                {toHijri(date, i18n.language)}
              </Typography>
            )}
          </Box>
          {!can(P.backdate) && (
            <Typography variant="caption" color="text.secondary"
              sx={{ alignSelf: 'center' }}>
              {t('entry.todayOnly')}
            </Typography>
          )}
        </Stack>

        {!isMobile && (
          <Box sx={{ display: 'grid', gridTemplateColumns: GRID, px: 2, py: 1, gap: 1,
                     bgcolor: c.surfaceSunken, borderTop: `1px solid ${c.border}`,
                     fontSize: 12, fontWeight: 500, color: c.inkMuted }}>
            <span style={{ whiteSpace: 'nowrap' }}>{t('entry.drcr')}</span>
            <span style={{ whiteSpace: 'nowrap' }}>{bi('entry.particulars')}</span>
            <span style={{ textAlign: 'end', whiteSpace: 'nowrap' }}>{bi('entry.debit')}</span>
            <span style={{ textAlign: 'end', whiteSpace: 'nowrap' }}>{bi('entry.credit')}</span>
            <span />
          </Box>
        )}

        <Box className="ruled" sx={{ borderTop: `1px solid ${c.border}` }}>
          {rows.map((row, i) => {
            const led = row.code ? byCode(row.code) : undefined
            const bal = row.code ? balances.of(row.code) : undefined
            return (
              <Box key={row.id} onFocusCapture={() => setActiveRow(row.id)} sx={{
                display: 'grid', gridTemplateColumns: GRID,
                alignItems: 'center', gap: 1, px: 2,
                minHeight: tokens.rowHeight + 10,
                '&:focus-within': { bgcolor: 'var(--surface-selected)' },
              }}>
                <Box role="button" tabIndex={-1}
                  aria-label={t('entry.switchSide')}
                  onClick={() => setRow(row.id,
                    { dr_cr: row.dr_cr === 'D' ? 'C' : 'D' })}
                  sx={{
                    fontFamily: tokens.mono, fontSize: 12, fontWeight: 600,
                    cursor: 'pointer', userSelect: 'none', color: c.inkMuted,
                  }}>
                  {row.dr_cr === 'D' ? 'Dr' : 'Cr'}
                </Box>

                <Box sx={{ minWidth: 0 }}>
                  <LedgerPicker
                    ref={(h) => { pickerRefs.current[row.id] = h }}
                    value={row.code}
                    groups={row.groups}
                    allowCodes={allowFor(row.groups)}
                    autoFocus={i === config.focusRow}
                    balanceOf={balances.of}
                    onChange={(code) => setRow(row.id, { code })}
                    onCommit={() => focusAmount(row.id)}
                    onCreateRequest={(typed) => {
                      setCreateSeed(typed); setCreateForRow(row.id); setCreateOpen(true)
                    }}
                  />
                  {led && (
                    <Typography component="div" sx={{
                      fontSize: 12, color: c.inkMuted, px: 1, mt: '-2px',
                      lineHeight: 1.35, whiteSpace: 'nowrap', overflow: 'hidden',
                      textOverflow: 'ellipsis',
                    }}>
                      <span className="doc-no" style={{ fontWeight: 400 }}>{led.code}</span>
                      {bal !== undefined && (
                        <span> · {t('entry.balanceShort')}{' '}
                          <span className="num" style={{ textAlign: 'start' }}>{fmt(bal)}</span>
                        </span>
                      )}
                      {led.maintain_bill_wise && (
                        <Box component="span"
                          onClick={() => setBillRow(row.id)}
                          sx={{
                            ml: 1, cursor: 'pointer',
                            color: row.bills?.length ? c.primaryInk : c.warningInk,
                            textDecoration: 'underline dotted',
                          }}>
                          {row.bills?.length
                            ? t('bills.allocatedCount', { n: row.bills.length })
                            : t('bills.allocate')}
                        </Box>
                      )}
                    </Typography>
                  )}
                </Box>

                {isMobile ? (
                  <AmountInput
                    ref={(h) => { amountRefs.current[row.id] = h }}
                    value={row.amount}
                    onChange={(v) => applyAmount(row.id, v)}
                    onCommit={() => advanceFrom(i)} />
                ) : (
                  <>
                    <Box>
                      {row.dr_cr === 'D' && (
                        <AmountInput
                          ref={(h) => { amountRefs.current[row.id] = h }}
                          value={row.amount}
                          onChange={(v) => applyAmount(row.id, v)}
                          onCommit={() => advanceFrom(i)} />
                      )}
                    </Box>
                    <Box>
                      {row.dr_cr === 'C' && (
                        <AmountInput
                          ref={(h) => { amountRefs.current[row.id] = h }}
                          value={row.amount}
                          onChange={(v) => applyAmount(row.id, v)}
                          onCommit={() => advanceFrom(i)} />
                      )}
                    </Box>
                  </>
                )}

                <Tooltip title={t('entry.removeLine')}>
                  <span>
                    <IconButton size="small" tabIndex={-1}
                      aria-label={t('entry.removeLine')}
                      disabled={rows.length <= 2}
                      onClick={() => removeRow(row.id)}>
                      <DeleteOutlineIcon sx={{ fontSize: 17 }} />
                    </IconButton>
                  </span>
                </Tooltip>
              </Box>
            )
          })}
        </Box>

        {/* Totals -- the moment it balances is visible, not discovered. */}
        <Box sx={{
          display: 'grid', gridTemplateColumns: GRID, alignItems: 'center',
          gap: 1, px: 2, py: 1.25, borderTop: `2px solid ${c.ink}`,
        }}>
          <Box />
          <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap" useFlexGap>
            <Button size="small" startIcon={<AddIcon />} onClick={() => addRow()}
              sx={{ color: c.inkMuted }}>{t('entry.addLine')}</Button>
            {!isMobile && balanceChip}
          </Stack>
          {isMobile ? (
            <span className="num" style={{ fontWeight: 700 }}>{display(totals.dr)}</span>
          ) : (
            <>
              <span className="num" style={{ fontWeight: 700 }}>{display(totals.dr)}</span>
              <span className="num" style={{ fontWeight: 700 }}>{display(totals.cr)}</span>
            </>
          )}
          <Box />
        </Box>
        {isMobile && <Box sx={{ px: 2, pb: 1.5 }}>{balanceChip}</Box>}

        <Box sx={{ px: 2, pb: 2, pt: 1 }}>
          <TextField inputRef={narrationRef} label={bi('entry.narration')}
            value={narration} fullWidth
            onChange={(e) => setNarration(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); void save() }
            }} />
        </Box>

        {error && (
          <Box sx={{ px: 2, pb: 2 }}>
            <Alert severity="error" onClose={() => setError(null)}>{error}</Alert>
          </Box>
        )}

        {/* The shortcut bar: stays in view while a long voucher scrolls. */}
        <Stack direction="row" spacing={1.5} alignItems="center" useFlexGap
          sx={{
            px: 2, py: 1.25, flexWrap: 'wrap', borderTop: `1px solid ${c.border}`,
            bgcolor: c.surface,
            position: { sm: 'sticky' }, bottom: 0, zIndex: 2,
            boxShadow: { sm: 'var(--shadow-footer)' },
          }}>
          {!isMobile && (
            <Stack direction="row" spacing={2} alignItems="center" useFlexGap
              sx={{ flexWrap: 'wrap', fontSize: 12, color: c.inkMuted }}>
              <span><Keycap combo="alt+a" /> {t('entry.addLine').toLowerCase()}</span>
              <span><Keycap combo="alt+b" /> {t('entry.bills')}</span>
              <span><Keycap combo="alt+c" /> {t('entry.createParty')}</span>
              <span><Keycap combo="escape" /> {t('common.back')}</span>
            </Stack>
          )}
          <Box sx={{ flex: 1 }} />
          <Button variant="contained" size="large"
            disabled={busy || !totals.balanced} onClick={() => void save()}
            sx={{ minWidth: 180, width: { xs: '100%', sm: 'auto' } }}>
            {busy ? t('common.loading') : t('entry.save')}
            {!isMobile && (
              <Box component="span" className="doc-no"
                sx={{ ml: 1, fontSize: 11, opacity: 0.8 }}>Ctrl+Enter</Box>
            )}
          </Button>
        </Stack>
      </Paper>

      {showPanel && !isMobile && (
        <Box component="aside" aria-label={t('entry.selectedLedger')}
          sx={{ flex: '1 1 280px', minWidth: 0, display: 'flex', flexDirection: 'column',
                gap: 2, position: { lg: 'sticky' }, top: { lg: 76 } }}>
          <Paper variant="outlined" sx={{ p: 2.25 }}>
            <Stack direction="row" justifyContent="space-between" alignItems="center">
              <span className="section-label">{t('entry.selectedLedger')}</span>
              {activeLed && can(P.reportOperational) && (
                <Button size="small" sx={{ minWidth: 0, color: c.primaryInk }}
                  onClick={() => guard(() =>
                    nav(`/ledger?code=${encodeURIComponent(activeLed.code)}`))}>
                  {t('entry.openLedger')}
                </Button>
              )}
            </Stack>
            {activeLed ? (
              <>
                <Typography dir="auto" sx={{ fontSize: 16, fontWeight: 600, mt: 1,
                                             textAlign: 'start' }}>
                  {(ar && activeLed.name_ar) || activeLed.name_en}
                </Typography>
                <Typography sx={{ fontSize: 12, color: c.inkMuted }} className="doc-no">
                  {activeLed.code}
                </Typography>
                {activeNow !== undefined ? (
                  <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
                             gap: 1, mt: 1.5 }}>
                    <Box sx={{ p: 1.25, borderRadius: '8px', bgcolor: c.surfaceSunken,
                               border: `1px solid ${c.divider}` }}>
                      <Typography sx={{ fontSize: 12, color: c.inkMuted }}>{t('entry.now')}</Typography>
                      <Typography className="num" sx={{ fontWeight: 600, textAlign: 'start' }}>
                        {fmt(activeNow)}</Typography>
                    </Box>
                    <Box sx={{ p: 1.25, borderRadius: '8px', bgcolor: c.primarySoft }}>
                      <Typography sx={{ fontSize: 12, color: c.primaryInk }}>{t('entry.afterSave')}</Typography>
                      <Typography className="num" sx={{ fontWeight: 700, textAlign: 'start',
                                                         color: c.primaryInk }}>
                        {fmt(activeAfter)}</Typography>
                    </Box>
                  </Box>
                ) : (
                  <Typography sx={{ fontSize: 13, color: c.inkMuted, mt: 1 }}>
                    {t('entry.balanceHidden')}
                  </Typography>
                )}
              </>
            ) : (
              <Typography sx={{ fontSize: 13, color: c.inkMuted, mt: 1 }}>
                {t('entry.noAccountYet')}
              </Typography>
            )}
          </Paper>

          {preview.length > 0 && (
            <Paper variant="outlined" sx={{ p: 2.25 }}>
              <span className="section-label">{t('entry.postingPreview')}</span>
              <Box sx={{ mt: 1 }}>
                {preview.map((p) => {
                  const l = byCode(p.code)
                  return (
                    <Stack key={p.code} direction="row" justifyContent="space-between"
                      alignItems="flex-end" spacing={1}
                      sx={{ py: 1, borderBottom: `1px solid ${c.divider}` }}>
                      <Box sx={{ minWidth: 0 }}>
                        <Typography dir="auto" sx={{ fontSize: 13, fontWeight: 500,
                                          whiteSpace: 'nowrap', textAlign: 'start',
                                          overflow: 'hidden', textOverflow: 'ellipsis' }}>
                          {(ar && l?.name_ar) || l?.name_en || p.code}
                        </Typography>
                        <Typography className="num" sx={{ fontSize: 12, color: c.inkMuted,
                                                           textAlign: 'start' }}>
                          {fmt(p.before)} →
                        </Typography>
                      </Box>
                      <Typography className="num" sx={{ fontSize: 13, fontWeight: 600,
                                                         whiteSpace: 'nowrap' }}>
                        {fmt(p.after)}
                      </Typography>
                    </Stack>
                  )
                })}
              </Box>
            </Paper>
          )}
        </Box>
      )}

      <CreatePartyDialog
        open={createOpen}
        initialName={createSeed}
        defaultKind={config.kind === 'PURCHASE' ? 'supplier' : 'customer'}
        onClose={() => setCreateOpen(false)}
        onCreated={(led: Ledger) => {
          if (createForRow != null) {
            setRow(createForRow, { code: led.code })
            setTimeout(() => amountRefs.current[createForRow]?.focus(), 40)
          }
        }} />

      {billRow != null && (() => {
        const row = rows.find((r) => r.id === billRow)
        const led = row?.code ? byCode(row.code) : undefined
        if (!row || !led) return null
        // A sale or purchase OPENS a bill; anything else settles one.
        const mode = (config.kind === 'SALES' || config.kind === 'PURCHASE')
          ? 'NEW' as const : 'AGAINST' as const
        return (
          <BillAllocationDialog
            open ledgerCode={led.code} ledgerName={led.name_en}
            amount={row.amount} mode={mode} initial={row.bills ?? []}
            onClose={() => setBillRow(null)}
            onApply={(bills) => setRow(row.id, { bills })} />
        )
      })()}

      <Snackbar open={!!saved} autoHideDuration={2600}
        onClose={() => setSaved(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
        <Alert severity="success" variant="filled" onClose={() => setSaved(null)}>
          {t('entry.posted', { no: saved })}
        </Alert>
      </Snackbar>
    </Box>
  )
}
